/**
 * The intention to attach another way of signing in, remembered across the trip to the provider.
 *
 * Attaching a second identity and signing in look identical on the way back: the same provider
 * returns the same kind of credential to the same address. Without something to tell them apart,
 * returning from an attach would be read as a sign-in and would switch to whichever account that
 * identity belongs to — which is the opposite of what the person asked for, and would move their
 * work out from under them.
 *
 * It is a flag, not a credential, and it lives in `sessionStorage`: one tab, one trip, gone when
 * the tab is. Storage that refuses is not an error — the attach simply cannot be started.
 */
export class Linking {
	static KEY = 'beyond.accounts.linking';

	/** Whether this tab left to attach an identity rather than to sign in */
	static pending(storage: Pick<Storage, 'getItem'> | undefined = globalThis.sessionStorage): boolean {
		return Boolean(Linking.purpose(storage));
	}

	/**
	 * Why this tab left: `attach` (another way to sign in) or `password` (proving the account's own
	 * provider before adding email sign-in, ACC-10); undefined for a sign-in
	 */
	static purpose(storage: Pick<Storage, 'getItem'> | undefined = globalThis.sessionStorage): 'attach' | 'password' | undefined {
		try {
			const value = storage?.getItem(Linking.KEY);
			return value === 'yes' ? 'attach' : value === 'password' ? 'password' : undefined;
		} catch {
			return undefined;
		}
	}

	static begin(storage: Pick<Storage, 'setItem'> | undefined = globalThis.sessionStorage, purpose: 'attach' | 'password' = 'attach'): void {
		try {
			storage?.setItem(Linking.KEY, purpose === 'attach' ? 'yes' : purpose);
		} catch {
			// Without storage the intention cannot survive the trip, and the return is a sign-in
		}
	}

	static end(storage: Pick<Storage, 'removeItem'> | undefined = globalThis.sessionStorage): void {
		try {
			storage?.removeItem(Linking.KEY);
		} catch {
			// Nothing to clear
		}
	}
}
